<template>
  <v-dialog v-model="open" max-width="420" persistent>
    <v-card class="cpd-card" elevation="0">

      <div class="cpd-head">
        <div class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <div class="cpd-icon-box mr-3">
              <v-icon color="#f59e0b" size="20">mdi-swap-horizontal</v-icon>
            </div>
            <div>
              <div class="cpd-title">แลกคะแนน → เงินสด</div>
              <div class="cpd-sub">คะแนนคงเหลือ: <b>{{ formatNum(balance) }}</b> คะแนน</div>
            </div>
          </div>
          <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
        </div>
      </div>

      <v-divider />

      <div class="cpd-body">
        <!-- Rate info -->
        <div class="cpd-rate-box">
          <v-icon color="#f59e0b" size="16" class="mr-1">mdi-information-outline</v-icon>
          อัตราแลกเปลี่ยน: <b>5,000 คะแนน = 10 บาท</b>
          <span class="ml-2 grey--text">(ขั้นต่ำ 5,000 คะแนน)</span>
        </div>

        <div class="field-label">จำนวนคะแนนที่ต้องการแลก</div>
        <v-text-field
          v-model.number="points"
          type="number"
          min="5000"
          step="5000"
          placeholder="เช่น 5000"
          outlined dense hide-details="auto"
          class="rounded-lg mb-2"
        />

        <!-- Quick amounts -->
        <div class="quick-row mb-4">
          <v-chip
            v-for="n in quickOptions"
            :key="n"
            label
            class="quick-chip"
            :disabled="n > balance"
            @click="points = n"
          >{{ n.toLocaleString('th-TH') }}</v-chip>
        </div>

        <!-- Preview -->
        <div v-if="cashPreview > 0" class="cpd-preview">
          <div class="cpd-preview__row">
            <span>คะแนนที่แลก</span>
            <b>{{ Number(points).toLocaleString('th-TH') }} คะแนน</b>
          </div>
          <div class="cpd-preview__row cpd-preview__row--highlight">
            <span>เงินที่จะได้รับ</span>
            <b class="cpd-preview__cash">฿{{ formatNum(cashPreview) }}</b>
          </div>
        </div>

        <div v-if="validationError" class="cpd-error mt-2">
          <v-icon x-small color="#ef4444" class="mr-1">mdi-alert-circle</v-icon>
          {{ validationError }}
        </div>
      </div>

      <v-divider />

      <div class="cpd-actions">
        <v-btn class="btn-outline" height="40" @click="close">ยกเลิก</v-btn>
        <v-btn
          class="btn-convert"
          height="40"
          :disabled="!canSubmit"
          :loading="loading"
          @click="submit"
        >
          <v-icon left small>mdi-check</v-icon>
          ยืนยันแลก
        </v-btn>
      </div>

    </v-card>
  </v-dialog>
</template>

<script>
import WalletService from "@/services/api/wallet";
import swalAlert from "@/services/alert/swalServices";

const RATE = 500;       // คะแนนต่อ 1 บาท
const MIN  = 5000;      // ขั้นต่ำ

export default {
  name: "ConvertPointsDialog",
  props: {
    value:   { type: Boolean, default: false },
    balance: { type: Number,  default: 0 },
    role:    { type: String,  default: "user" },
  },
  emits: ["input", "done"],
  data() {
    return {
      loading: false,
      points: null,
      quickOptions: [5000, 10000, 25000, 50000],
    };
  },
  computed: {
    open: {
      get() { return this.value; },
      set(v) { this.$emit("input", v); },
    },
    cashPreview() {
      const p = Math.floor(Number(this.points) || 0);
      if (p < MIN || p % RATE !== 0) return 0;
      return Math.round((p / RATE) * 100) / 100;
    },
    validationError() {
      const p = Math.floor(Number(this.points) || 0);
      if (!this.points) return null;
      if (p < MIN) return `ขั้นต่ำ ${MIN.toLocaleString()} คะแนน`;
      if (p > this.balance) return `คะแนนไม่เพียงพอ (มี ${this.balance.toLocaleString()})`;
      if (p % RATE !== 0) return `คะแนนต้องเป็นจำนวนที่หารด้วย ${RATE.toLocaleString()} ลงตัว`;
      return null;
    },
    canSubmit() {
      return !this.validationError && this.cashPreview > 0;
    },
  },
  methods: {
    close() {
      this.open = false;
      this.points = null;
    },
    formatNum(n) {
      return Number(n || 0).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    async submit() {
      if (!this.canSubmit) return;
      this.loading = true;
      try {
        const res = await WalletService.convertPointsToCash(this.points, this.role);
        const d = res.data;
        await swalAlert.Success(
          "แลกคะแนนสำเร็จ",
          `ได้รับ ฿${this.formatNum(d.cashReceived)} เข้ากระเป๋า Cash`
        );
        this.close();
        this.$emit("done");
      } catch (e) {
        await swalAlert.Error("ไม่สำเร็จ", e?.response?.data?.message || "เกิดข้อผิดพลาด");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.cpd-card {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #f1f5f9;
}

.cpd-head {
  padding: 18px 20px 14px;
}

.cpd-icon-box {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #fef3c7;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cpd-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.cpd-sub {
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
}

.cpd-body {
  padding: 16px 20px 10px;
}

.cpd-rate-box {
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 13px;
  color: #92400e;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.field-label {
  font-size: 13px;
  font-weight: 600;
  color: #1f2a39;
  margin-bottom: 6px;
}

.quick-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

::v-deep .quick-chip {
  background: #fef3c7 !important;
  border: 1px solid #fcd34d !important;
  font-weight: 700 !important;
  color: #92400e !important;
  border-radius: 8px !important;
  cursor: pointer;
}

::v-deep .quick-chip.v-chip--disabled {
  opacity: 0.4 !important;
  cursor: not-allowed;
}

.cpd-preview {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 12px;
  padding: 12px 16px;
}

.cpd-preview__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: #374151;
  padding: 3px 0;
}

.cpd-preview__row--highlight {
  border-top: 1px dashed #bbf7d0;
  margin-top: 6px;
  padding-top: 8px;
}

.cpd-preview__cash {
  font-size: 20px;
  font-weight: 800;
  color: #059669;
}

.cpd-error {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #ef4444;
}

.cpd-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 20px 18px;
}

.btn-outline {
  background: #fff !important;
  color: #1f2937 !important;
  border: 1px solid #e5e7eb !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
}

.btn-convert {
  background: #f59e0b !important;
  color: #fff !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 700 !important;
}
</style>
